
export enum AssetType {
  Image = 'image',
  Video = 'video',
  Caption = 'caption'
}

export enum AssetStatus {
  Draft = 'draft', // Legacy
  Available = 'available',
  Approved = 'approved', // Legacy
  Reserved = 'reserved',
  Deployed = 'deployed',
  Archived = 'archived'
}

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
  link?: string;
  assetId?: string;
}

export interface DeploymentLog {
  id: string;
  platform: string;
  deployedAt: number;
  deployedBy: string;
  deployedByName: string;
  notes?: string;
  externalUrl?: string;
  campaignId?: string;
}

export interface Asset {
  id: string;
  url: string; // Image URL, Video URL, or Text content
  thumbnailUrl?: string;
  type: AssetType;
  createdAt: number;
  scenario: string; // Acts as the Name/Title
  format?: string;
  status: AssetStatus;
  campaignId?: string;
  tags: string[];
  rating?: number;
  meta: Record<string, any>; // Extra generation params
  
  // Attribution
  createdBy: string;
  createdByName?: string;
  createdByAvatar?: string;
  
  // Deployment Tracking
  deployments?: DeploymentLog[];
  
  // Context
  generationPrompt?: string;
}

export interface Campaign {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'planning' | 'completed' | 'archived';
  assetCount: number;
  startDate: string;
  endDate: string;
  chipRole?: string;
  narrativeIds?: string[];
  parentId?: string; // For subfolder support
}

export interface NarrativeSeason {
  id: string;
  title: string;
  status?: string;
  episodes: NarrativeEpisode[];
}

export interface NarrativeEpisode {
  id: string;
  title: string;
  concept: string;
  status: 'idea' | 'scripted' | 'production' | 'published';
  platform: string;
}

export interface ChipDNA {
  version: number;
  lastUpdated?: number;
  core: {
    name: string;
    tagline: string;
    mission: string;
    values: string[];
    backstory: string;
    archetype: string;
    bio: string; // Added Bio field
  };
  visual: {
    body: string;
    face: string;
    restrictions: string[];
    likenessImage?: string | null; // URL of the reference image (Firebase Storage)
  };
  voice: {
    tone: string[];
    styleGuide: string; // Description of vocal qualities
    keywords: string[];
    dos: string[];
    donts: string[];
    samplePhrases: string[];
  };
  personality: {
    traits: string[];
    emotionalGoals: string[];
    interests: string[];
    favorites: Record<string, string>; // Food, Song, Activity, etc.
  };
}

export interface GitHubConfig {
  token?: string;
  owner?: string;
  repo?: string;
  branch?: string;
  lastSync?: number;
}

export interface UserProfile {
  role?: string;
  title?: string;
  onboardingCompleted?: boolean;
  photoURL?: string; // Stored in Firestore (Base64 or URL)
  github?: GitHubConfig;
}

// Dynamic Options for Visual Studio
export interface ScenarioOption {
  id: string;
  name: string;
  icon: string;
  prompt: string;
  isCustom?: boolean;
}

export interface EnvironmentOption {
  id: string;
  name: string;
  isCustom?: boolean;
}

export interface AttireOption {
  id: string;
  name: string;
  isCustom?: boolean;
}

export type GenerationStatus = 'idle' | 'generating' | 'success' | 'error';
